import React, { useState } from 'react';
import {
  Box, Typography, Chip, Accordion, AccordionSummary,
  AccordionDetails, LinearProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CodeIcon from '@mui/icons-material/Code';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import { jsPDF } from 'jspdf';
import DownloadIcon from '@mui/icons-material/Download';
import { FONT_BODY, FONT_MONO, RADIUS } from '../../styles/recruiterTheme';

const optionLabel = (i) => String.fromCharCode(65 + i);

const ScoreBadge = ({ score, max, color }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.5,
    background: color?.bg || '#F3EEF9',
    color: color?.text || '#4F378B',
    border: `1.5px solid ${color?.border || '#D0BCFF'}`,
    borderRadius: `${RADIUS.pill}px`, px: 1.5, height: 24, boxSizing: 'border-box',
    fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700,
  }}>
    {score !== null && score !== undefined ? score : '—'}
    {max !== undefined && <span style={{ opacity: 0.6 }}>/{max}</span>}
  </Box>
);

// ─── MCQ Results ──────────────────────────────────────────────────────────────
const MCQResults = ({ questions }) => {
  if (!questions?.length)
    return <EmptyState icon={<QuizIcon />} text="No MCQ answers recorded." />;

  const totalScore   = questions.reduce((s, q) => s + (q.partial_score || 0), 0);
  const totalPossible = questions.length;
  const pct = Math.round((totalScore / totalPossible) * 100);

  return (
    <Box>
      {/* Summary bar */}
      <SummaryBar
        label="MCQ Score"
        score={totalScore.toFixed(1)}
        max={totalPossible}
        pct={pct}
        color="#6750A4"
      />

      {questions.map((q, idx) => {
        const isCorrect  = q.is_correct;
        const isPartial  = !isCorrect && q.partial_score > 0;
        const userAns    = Array.isArray(q.user_answer)  ? q.user_answer  : [q.user_answer];
        const correctAns = Array.isArray(q.correct_answer) ? q.correct_answer : [q.correct_answer];

        return (
          <QuestionCard key={idx} status={isCorrect ? 'correct' : isPartial ? 'partial' : 'wrong'}>
            {/* Question header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <QNum>{idx + 1}</QNum>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: '#1C1B1F', lineHeight: 1.5 }}>
                  {q.question}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {isCorrect
                  ? <Chip icon={<CheckCircleIcon />} label="Correct" size="small" sx={{ background: '#E6F4EA', color: '#2D7D46', fontWeight: 700, fontSize: 11 }} />
                  : isPartial
                    ? <Chip label={`Partial (${q.partial_score})`} size="small" sx={{ background: '#FFF3E0', color: '#E65100', fontWeight: 700, fontSize: 11 }} />
                    : <Chip icon={<CancelIcon />} label="Wrong" size="small" sx={{ background: '#FDECEA', color: '#B3261E', fontWeight: 700, fontSize: 11 }} />
                }
                <ScoreBadge
                  score={q.partial_score?.toFixed ? q.partial_score.toFixed(2) : q.partial_score}
                  max={1}
                  color={isCorrect ? { bg: '#E6F4EA', text: '#2D7D46', border: '#A5D6A7' } : { bg: '#FDECEA', text: '#B3261E', border: '#FFCDD2' }}
                />
              </Box>
            </Box>

            {/* Options (if available) */}
            {q.options?.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ml: '36px', mb: 1.5 }}>
                {q.options.map((opt, i) => {
                  const optClean   = opt?.trim().toLowerCase();
                  const isUserPick = userAns.some(a => a?.toLowerCase() === optClean || optClean?.includes(a?.toLowerCase()));
                  const isCorrectOpt = correctAns.some(a => a?.toLowerCase() === optClean || optClean?.includes(a?.toLowerCase()));

                  return (
                    <OptionRow key={i} picked={isUserPick} correct={isCorrectOpt}>
                      <span style={{ fontWeight: 700, minWidth: 18 }}>{optionLabel(i)}.</span>
                      <span style={{ flex: 1 }}>{opt}</span>
                      {isCorrectOpt && <CheckCircleIcon sx={{ fontSize: 14, color: '#2D7D46' }} />}
                      {isUserPick && !isCorrectOpt && <CancelIcon sx={{ fontSize: 14, color: '#B3261E' }} />}
                    </OptionRow>
                  );
                })}
              </Box>
            )}

            {/* Answer comparison (when no options stored) */}
            {!q.options?.length && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, ml: '36px' }}>
                <AnswerBox label="Candidate's Answer" value={userAns.join(', ') || '(no answer)'} color="#1565C0" bg="#E3F2FD" border="#90CAF9" />
                <AnswerBox label="Correct Answer" value={correctAns.join(', ')} color="#2D7D46" bg="#E6F4EA" border="#A5D6A7" />
              </Box>
            )}
          </QuestionCard>
        );
      })}
    </Box>
  );
};

// ─── Subjective Results ───────────────────────────────────────────────────────
const SubjectiveResults = ({ questions }) => {
  if (!questions?.length)
    return <EmptyState icon={<MenuBookIcon />} text="No theory answers recorded." />;

  const scored    = questions.filter(q => q.score !== null && q.score !== undefined);
  const totalScore = scored.reduce((s, q) => s + (q.score || 0), 0);
  const totalMax   = questions.reduce((s, q) => s + (q.max_score || 5), 0);
  const pct = totalMax ? Math.round((totalScore / totalMax) * 100) : 0;

  return (
    <Box>
      <SummaryBar label="Theory Score" score={totalScore} max={totalMax} pct={pct} color="#6750A4" />

      {questions.map((q, idx) => {
        const hasAnswer  = q.user_answer?.trim();
        const keyPoints  = Array.isArray(q.key_points)
          ? q.key_points
          : (q.key_points ? String(q.key_points).split('\n').filter(Boolean) : []);
        const scorePct   = q.score !== null && q.max_score
          ? Math.round((q.score / q.max_score) * 100) : null;

        return (
          <QuestionCard key={idx} status={
            q.score === null ? 'pending'
            : scorePct >= 70 ? 'correct'
            : scorePct >= 40 ? 'partial' : 'wrong'
          }>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <QNum>{idx + 1}</QNum>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: '#1C1B1F', lineHeight: 1.5, mb: 0.5 }}>
                  {q.question}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {q.skill && <Chip label={q.skill} size="small" sx={{ fontSize: 11, fontWeight: 700, background: '#E8DEF8', color: '#4F378B' }} />}
                  {q.difficulty && <Chip label={q.difficulty} size="small" sx={{ fontSize: 11, fontWeight: 700, background: '#FFF3E0', color: '#E65100' }} />}
                </Box>
              </Box>
              <ScoreBadge
                score={q.score ?? '—'}
                max={q.max_score || 5}
                color={
                  q.score === null
                    ? { bg: '#F3EEF9', text: '#8B7DB5', border: '#D0BCFF' }
                    : scorePct >= 70
                      ? { bg: '#E6F4EA', text: '#2D7D46', border: '#A5D6A7' }
                      : scorePct >= 40
                        ? { bg: '#FFF3E0', text: '#E65100', border: '#FFCC80' }
                        : { bg: '#FDECEA', text: '#B3261E', border: '#FFCDD2' }
                }
              />
            </Box>

            {/* Candidate answer */}
            <AnswerBox
              label="Candidate's Answer"
              value={hasAnswer || '(no answer provided)'}
              color="#1565C0" bg="#E3F2FD" border="#90CAF9"
              empty={!hasAnswer}
              sx={{ ml: '36px', mb: 1 }}
            />

            {/* Reference answer */}
            {q.reference_answer && (
              <Accordion disableGutters elevation={0} sx={{ ml: '36px', mt: 1, border: '1px solid #C8E6C9', borderRadius: `${RADIUS.sm}px !important`, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, px: 1.5, background: '#F1FAF3', borderRadius: `${RADIUS.sm}px` }}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color: '#2D7D46', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    <CheckCircleIcon sx={{ fontSize: 12, mr: 0.5 }} /> Reference Answer
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, py: 1, background: '#F1FAF3' }}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: '#1A3D23', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {q.reference_answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Key points */}
            {keyPoints.length > 0 && (
              <Accordion disableGutters elevation={0} sx={{ ml: '36px', mt: 1, border: '1px solid #90CAF9', borderRadius: `${RADIUS.sm}px !important`, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, px: 1.5, background: '#E3F2FD', borderRadius: `${RADIUS.sm}px` }}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color: '#1565C0', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Key Points ({keyPoints.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, py: 1, background: '#E3F2FD' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {keyPoints.map((kp, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Box sx={{ width: 18, height: 18, borderRadius: '50%', background: '#1565C0', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
                          {i + 1}
                        </Box>
                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: '#0D2D5E', lineHeight: 1.5 }}>
                          {kp}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </QuestionCard>
        );
      })}
    </Box>
  );
};

// ─── Programming Results ──────────────────────────────────────────────────────
const ProgrammingResults = ({ questions }) => {
  if (!questions?.length)
    return <EmptyState icon={<CodeIcon />} text="No coding answers recorded." />;

  const scored     = questions.filter(q => q.score !== null && q.score !== undefined);
  const totalScore = scored.reduce((s, q) => s + (q.score || 0), 0);
  const totalMax   = questions.length * 5;
  const pct        = totalMax ? Math.round((totalScore / totalMax) * 100) : 0;

  const categoryColors = {
    Coding:     { bg: '#E3F2FD', color: '#1565C0' },
    Conceptual: { bg: '#F3E5F5', color: '#6A1B9A' },
    Debugging:  { bg: '#FFEBEE', color: '#B71C1C' },
    Tooling:    { bg: '#E8F5E9', color: '#1B5E20' },
  };

  return (
    <Box>
      <SummaryBar label="Coding Score" score={totalScore} max={totalMax} pct={pct} color="#6750A4" />

      {questions.map((q, idx) => {
        const category  = q.category || 'Coding';
        const catColor  = categoryColors[category] || categoryColors.Coding;
        const scorePct  = q.score !== null && q.score !== undefined ? Math.round((q.score / 5) * 100) : null;
        const primaryQ  = q.question || q.problem_statement || q.scenario || q.task || '';

        return (
          <QuestionCard key={idx} status={
            q.score === null || q.score === undefined ? 'pending'
            : scorePct >= 70 ? 'correct'
            : scorePct >= 40 ? 'partial' : 'wrong'
          }>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <QNum>{idx + 1}</QNum>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: '#1C1B1F', lineHeight: 1.5, mb: 0.75 }}>
                  {primaryQ}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  <Chip label={category} size="small" sx={{ fontSize: 11, fontWeight: 700, background: catColor.bg, color: catColor.color }} />
                  {q.skill && <Chip label={q.skill} size="small" sx={{ fontSize: 11, fontWeight: 700, background: '#E8DEF8', color: '#4F378B' }} />}
                  {q.difficulty && <Chip label={q.difficulty} size="small" sx={{ fontSize: 11, fontWeight: 700, background: '#FFF3E0', color: '#E65100' }} />}
                  {q.language && <Chip icon={<CodeIcon sx={{ fontSize: 11 }} />} label={q.language} size="small" sx={{ fontSize: 11, fontWeight: 700, background: '#F3EEF9', color: '#4F378B' }} />}
                </Box>
              </Box>
              <ScoreBadge
                score={q.score ?? '—'}
                max={5}
                color={
                  q.score === null || q.score === undefined
                    ? { bg: '#F3EEF9', text: '#8B7DB5', border: '#D0BCFF' }
                    : scorePct >= 70
                      ? { bg: '#E6F4EA', text: '#2D7D46', border: '#A5D6A7' }
                      : scorePct >= 40
                        ? { bg: '#FFF3E0', text: '#E65100', border: '#FFCC80' }
                        : { bg: '#FDECEA', text: '#B3261E', border: '#FFCDD2' }
                }
              />
            </Box>

            {/* Extra context fields */}
            {/* Extra context fields */}
{q.constraints && (
  <InfoRow label="Constraints" value={q.constraints} color="#4F378B" bg="#F3EEF9" border="#D0BCFF" ml />
)}

{q.input_format && (
  <InfoRow label="Input Format" value={q.input_format} color="#1565C0" bg="#E3F2FD" border="#90CAF9" ml />
)}

{q.output_format && (
  <InfoRow label="Output Format" value={q.output_format} color="#1565C0" bg="#E3F2FD" border="#90CAF9" ml />
)}

{q.code_scenario && (
  <Box sx={{ ml: '36px', mb: 1 }}>
    <InfoLabel color="#B71C1C">Code / Scenario</InfoLabel>
    <CodeBlock>{q.code_scenario}</CodeBlock>
  </Box>
)}
{q.observed_behavior && (
  <InfoRow label="Observed Behavior" value={q.observed_behavior} color="#c62828" bg="#fff5f5" border="#ffcdd2" ml />
)}

{q.expected_discussion_points && (
  <InfoRow label="Expected Discussion Points" value={q.expected_discussion_points} color="#6A1B9A" bg="#F3E5F5" border="#CE93D8" ml />
)}

{q.task && (
  <InfoRow label="Task" value={q.task} color="#1B5E20" bg="#E8F5E9" border="#A5D6A7" ml />
)}

{/* Candidate code */}
<Box sx={{ ml: '36px', mt: 1 }}>
  <InfoLabel color="#1565C0">Candidate's Code</InfoLabel>
  {q.user_code?.trim()
    ? <CodeBlock lang={q.language}>{q.user_code}</CodeBlock>
    : <EmptyAnswer>No code submitted</EmptyAnswer>
  }
</Box>

  {/* Legacy fallback fields */}
  {q.example_input && (
    <InfoRow label="Example Input" value={q.example_input} color="#1565C0" bg="#E3F2FD" border="#90CAF9" ml sx={{ mt: 1 }} />
  )}
  {q.expected_output && (
    <InfoRow label="Expected Output" value={q.expected_output} color="#2D7D46" bg="#E6F4EA" border="#A5D6A7" ml sx={{ mt: 1 }} />
  )}

  {/* Examples array (input / output / explanation) */}
  {Array.isArray(q.examples) && q.examples.length > 0 && (
    <Box sx={{ ml: '36px', mt: 1 }}>
      <InfoLabel color="#2D7D46">Examples</InfoLabel>
      {q.examples.map((ex, exIdx) => (
        <Box
          key={exIdx}
          sx={{
            border: '1px solid #A5D6A7', borderRadius: `${RADIUS.sm}px`,
            overflow: 'hidden', mb: 1,
          }}
        >
          <Box sx={{
            background: '#E8F5E9', color: '#1B5E20',
            fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700,
            px: 1.5, py: 0.5, letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            Example {exIdx + 1}
          </Box>
          <Box sx={{ p: '12px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {ex.input && (
              <Box>
                <InfoLabel color="#1565C0">Input</InfoLabel>
                <CodeBlock>{ex.input}</CodeBlock>
              </Box>
            )}
            {ex.output && (
              <Box>
                <InfoLabel color="#2D7D46">Output</InfoLabel>
                <CodeBlock>{ex.output}</CodeBlock>
              </Box>
            )}
            {ex.explanation && (
              <Box>
                <InfoLabel color="#6750A4">Explanation</InfoLabel>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: '#2D1B5E', lineHeight: 1.5 }}>
                  {ex.explanation}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )}

  {q.expected_approach && (
    <InfoRow label="Expected Approach" value={q.expected_approach} color="#2D7D46" bg="#E6F4EA" border="#A5D6A7" ml sx={{ mt: 1 }} />
  )}
          </QuestionCard>
        );
      })}
    </Box>
  );
};

// ─── Shared sub-components ────────────────────────────────────────────────────
const SummaryBar = ({ label, score, max, pct, color }) => (
  <Box sx={{ mb: 2, p: 2, background: '#F8F4FF', border: '1.5px solid #E8DEF8', borderRadius: `${RADIUS.md}px` }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, color: '#4F378B' }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700, color }}>
        {score}/{max} ({pct}%)
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={pct}
      sx={{ borderRadius: 4, height: 6, background: '#E8DEF8', '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${color}, #B69DF8)` } }}
    />
  </Box>
);

const QNum = ({ children }) => (
  <Box sx={{
    width: 24, height: 24, borderRadius: `${RADIUS.sm}px`, flexShrink: 0,
    background: 'linear-gradient(135deg, #4F378B, #6750A4)',
    color: '#fff', fontSize: 12, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: FONT_BODY,
  }}>
    {children}
  </Box>
);

const QuestionCard = ({ children, status }) => {
  const borderColor = status === 'correct' ? '#A5D6A7' : status === 'partial' ? '#FFCC80' : status === 'wrong' ? '#FFCDD2' : '#E8DEF8';
  const leftColor   = status === 'correct' ? '#2D7D46' : status === 'partial' ? '#E65100' : status === 'wrong' ? '#B3261E' : '#D0BCFF';
  return (
    <Box sx={{
      background: '#fff', border: `1.5px solid ${borderColor}`,
      borderLeft: `4px solid ${leftColor}`,
      borderRadius: `${RADIUS.md}px`, p: 2, mb: 1.5,
    }}>
      {children}
    </Box>
  );
};

const OptionRow = ({ children, picked, correct }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1,
    p: '8px 12px', borderRadius: `${RADIUS.sm}px`, fontSize: 13,
    fontFamily: FONT_BODY,
    background: correct ? '#E6F4EA' : picked ? '#FDECEA' : '#FAFAFA',
    border: `1.5px solid ${correct ? '#A5D6A7' : picked ? '#FFCDD2' : '#EEE'}`,
    color: correct ? '#1A3D23' : picked ? '#7F1D1D' : '#49454F',
    fontWeight: picked || correct ? 700 : 400,
  }}>
    {children}
  </Box>
);

const AnswerBox = ({ label, value, color, bg, border, empty, sx: sxProp }) => (
  <Box sx={{ ...sxProp }}>
    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ background: bg, border: `1.5px solid ${border}`, borderRadius: `${RADIUS.sm}px`, p: '8px 12px' }}>
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: empty ? '#aaa' : color, lineHeight: 1.5, whiteSpace: 'pre-wrap', fontStyle: empty ? 'italic' : 'normal' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const InfoRow = ({ label, value, color, bg, border, ml, sx: sxProp }) => (
  <Box sx={{ ...(ml ? { ml: '36px' } : {}), mb: 1, ...sxProp }}>
    <InfoLabel color={color}>{label}</InfoLabel>
    <Box sx={{ background: bg, border: `1.5px solid ${border}`, borderRadius: `${RADIUS.sm}px`, p: '8px 12px' }}>
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const InfoLabel = ({ children, color }) => (
  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>
    {children}
  </Typography>
);

const CodeBlock = ({ children, lang }) => (
  <Box sx={{
    background: '#1e1e2e', borderRadius: `${RADIUS.sm}px`, p: '12px 16px',
    fontFamily: FONT_MONO, fontSize: 12,
    color: '#cdd6f4', lineHeight: 1.5, whiteSpace: 'pre-wrap',
    overflowX: 'auto', border: '1px solid #313244',
  }}>
    {lang && (
      <Box sx={{ fontSize: 11, color: '#89b4fa', fontWeight: 700, mb: 0.75, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {lang}
      </Box>
    )}
    {children}
  </Box>
);

const EmptyAnswer = ({ children }) => (
  <Box sx={{ p: '8px 12px', background: '#FAFAFA', border: '1.5px dashed #DDD', borderRadius: `${RADIUS.sm}px` }}>
    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: '#aaa', fontStyle: 'italic' }}>
      {children}
    </Typography>
  </Box>
);

const EmptyState = ({ icon, text }) => (
  <Box sx={{ textAlign: 'center', py: 5, color: '#B0A0D0' }}>
    {React.cloneElement(icon, { sx: { fontSize: 40, mb: 1 } })}
    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 500, lineHeight: 1.5, color: '#8B7DB5' }}>{text}</Typography>
  </Box>
);

const generateResultsPdf = (candidateName, mcqQuestions, subjectiveQuestions, codingQuestions) => {
  const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
  const PAGE_W = 595, PAGE_H = 842, MARGIN = 40, CONTENT_W = PAGE_W - MARGIN * 2;
  let y = MARGIN;

  const checkNewPage = (h = 20) => {
    if (y + h > PAGE_H - MARGIN) { doc.addPage(); y = MARGIN; }
  };
  const printText = (text, x, fontSize, color, bold, maxWidth) => {
    doc.setFontSize(fontSize); doc.setTextColor(...color);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(String(text || ''), maxWidth);
    lines.forEach(line => { checkNewPage(fontSize + 4); doc.text(line, x, y); y += fontSize + 4; });
  };
  const printBox = (text, bgColor, borderColor, fontSize = 10) => {
    doc.setFontSize(fontSize); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
    const lines = doc.splitTextToSize(String(text || ''), CONTENT_W - 16);
    const boxH = lines.length * (fontSize + 4) + 12;
    checkNewPage(boxH + 8);
    doc.setFillColor(...bgColor); doc.setDrawColor(...borderColor);
    doc.roundedRect(MARGIN, y, CONTENT_W, boxH, 4, 4, 'FD');
    let ty = y + 8;
    lines.forEach(line => { doc.text(line, MARGIN + 8, ty); ty += fontSize + 4; });
    y += boxH + 6;
  };
  const printCodeBox = (text, fontSize = 9) => {
    doc.setFontSize(fontSize); doc.setFont('courier', 'normal');
    const lines = doc.splitTextToSize(String(text || '(no code submitted)'), CONTENT_W - 16);
    const boxH = lines.length * (fontSize + 3) + 12;
    checkNewPage(boxH + 8);
    doc.setFillColor(30, 30, 46); doc.setDrawColor(49, 50, 68);
    doc.roundedRect(MARGIN, y, CONTENT_W, boxH, 4, 4, 'FD');
    doc.setTextColor(205, 214, 244);
    let ty = y + 8;
    lines.forEach(line => { doc.text(line, MARGIN + 8, ty); ty += fontSize + 3; });
    y += boxH + 6;
  };
  const printLabel = (text, color) => printText(text, MARGIN, 10, color, true, CONTENT_W);
  const printDivider = () => {
    checkNewPage(12); doc.setDrawColor(210, 200, 230); doc.setLineWidth(0.5);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y); y += 10;
  };
  const printSectionTitle = (title) => {
    checkNewPage(30); y += 6;
    doc.setFillColor(79, 55, 139); doc.rect(MARGIN, y - 14, CONTENT_W, 22, 'F');
    doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
    doc.text(title, MARGIN + 8, y); y += 16;
  };

  // ── Header ──
  doc.setFontSize(22); doc.setFont('helvetica', 'bold'); doc.setTextColor(79, 55, 139);
  doc.text('Screening Test Results', MARGIN, y); y += 10;
  doc.setFontSize(11); doc.setFont('helvetica', 'normal'); doc.setTextColor(139, 125, 181);
  doc.text(candidateName || 'Candidate', MARGIN, y + 6); y += 22;
  printDivider();

  // ── MCQ Section ──
  const mcqTotal = mcqQuestions?.length || 0;
  const mcqScore = (mcqQuestions || []).reduce((s, q) => s + (q.partial_score || 0), 0);
  printSectionTitle(`MCQ  —  ${mcqScore.toFixed(1)}/${mcqTotal}`); y += 6;
  if (mcqTotal > 0) {
    mcqQuestions.forEach((q, i) => {
      checkNewPage(40);
      const userAns = Array.isArray(q.user_answer) ? q.user_answer.join(', ') : (q.user_answer || '(no answer)');
      const correctAns = Array.isArray(q.correct_answer) ? q.correct_answer.join(', ') : q.correct_answer;
      printText(`${i + 1}. ${q.question}`, MARGIN, 11, [30, 30, 30], true, CONTENT_W);
      const statusColor = q.is_correct ? [46, 125, 50] : [183, 28, 28];
      printText(`Status: ${q.is_correct ? 'Correct' : 'Incorrect'} (${q.partial_score ?? 0}/1)`, MARGIN, 10, statusColor, true, CONTENT_W);
      printText(`Candidate's Answer: ${userAns}`, MARGIN, 10, [21, 101, 192], false, CONTENT_W);
      printText(`Correct Answer: ${correctAns}`, MARGIN, 10, [46, 125, 50], false, CONTENT_W);
      y += 4;
      if (i !== mcqQuestions.length - 1) printDivider();
    });
  } else printText('No MCQ answers recorded.', MARGIN, 10, [150, 150, 150], false, CONTENT_W);

  // ── Theory Section ──
  y += 12;
  const subjTotal = (subjectiveQuestions || []).reduce((s, q) => s + (q.max_score || 5), 0);
  const subjScore = (subjectiveQuestions || []).reduce((s, q) => s + (q.score || 0), 0);
  printSectionTitle(`Theory  —  ${subjScore}/${subjTotal}`); y += 6;
  if (subjectiveQuestions?.length > 0) {
    subjectiveQuestions.forEach((q, i) => {
      checkNewPage(40);
      printText(`${i + 1}. ${q.question}`, MARGIN, 11, [30, 30, 30], true, CONTENT_W); y += 2;
      printText(`Score: ${q.score ?? '—'}/${q.max_score || 5}`, MARGIN, 10, [103, 80, 164], true, CONTENT_W);
      printLabel("Candidate's Answer:", [21, 101, 192]);
      printBox(q.user_answer?.trim() || '(no answer provided)', [227, 242, 253], [144, 202, 249]);
      if (q.reference_answer) { printLabel('Reference Answer:', [46, 125, 50]); printBox(q.reference_answer, [241, 250, 243], [200, 230, 201]); }
      y += 4;
      if (i !== subjectiveQuestions.length - 1) printDivider();
    });
  } else printText('No theory answers recorded.', MARGIN, 10, [150, 150, 150], false, CONTENT_W);

  // ── Coding Section ──
  y += 12;
  const codeTotal = (codingQuestions?.length || 0) * 5;
  const codeScore = (codingQuestions || []).reduce((s, q) => s + (q.score || 0), 0);
  printSectionTitle(`Coding  —  ${codeScore}/${codeTotal}`); y += 6;
  if (codingQuestions?.length > 0) {
    codingQuestions.forEach((q, i) => {
      checkNewPage(40);
      const primaryQ = q.question || q.problem_statement || q.scenario || q.task || '';
      printText(`${i + 1}. ${primaryQ}`, MARGIN, 11, [30, 30, 30], true, CONTENT_W); y += 2;
      printText(`Score: ${q.score ?? '—'}/5`, MARGIN, 10, [103, 80, 164], true, CONTENT_W);
      printLabel("Candidate's Code:", [21, 101, 192]);
      printCodeBox(q.user_code);
      y += 4;
      if (i !== codingQuestions.length - 1) printDivider();
    });
  } else printText('No coding answers recorded.', MARGIN, 10, [150, 150, 150], false, CONTENT_W);

  const safeName = (candidateName || 'candidate').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  doc.save(`test_results_${safeName}.pdf`);
};


// ─── Main Export ──────────────────────────────────────────────────────────────
const CandidateTestResults = ({ mcqQuestions, subjectiveQuestions, codingQuestions, candidateName }) => {
  const [activeTab, setActiveTab] = useState('mcq');

  const tabs = [
    { key: 'mcq',        label: 'MCQ',    icon: <QuizIcon />,     count: mcqQuestions?.length || 0 },
    { key: 'subjective', label: 'Theory', icon: <MenuBookIcon />, count: subjectiveQuestions?.length || 0 },
    { key: 'coding',     label: 'Coding', icon: <CodeIcon />,     count: codingQuestions?.length || 0 },
  ];

  return (
    <Box sx={{ fontFamily: FONT_BODY }}>
      {/* Tab bar + Download button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2, borderBottom: '2px solid #F0EAF9', pb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {tabs.map(tab => (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, height: 36, boxSizing: 'border-box', borderRadius: `${RADIUS.sm}px`, cursor: 'pointer',
                fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700,
                background: activeTab === tab.key ? 'linear-gradient(135deg, #4F378B, #6750A4)' : 'transparent',
                color: activeTab === tab.key ? '#fff' : '#8B7DB5',
                transition: 'all 0.2s',
                '&:hover': { background: activeTab === tab.key ? undefined : '#F3EEF9' },
              }}
            >
              {React.cloneElement(tab.icon, { sx: { fontSize: 16 } })}
              {tab.label}
              <Box sx={{
                background: activeTab === tab.key ? 'rgba(255,255,255,0.25)' : '#E8DEF8',
                color: activeTab === tab.key ? '#fff' : '#6750A4',
                borderRadius: `${RADIUS.pill}px`, px: 1, minWidth: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
              }}>
                {tab.count}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          component="button"
          onClick={() => generateResultsPdf(candidateName, mcqQuestions, subjectiveQuestions, codingQuestions)}
          sx={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: 'transparent', color: '#6750A4',
            fontFamily: FONT_BODY, fontWeight: 600, fontSize: 13,
            borderRadius: `${RADIUS.sm}px`, height: 36, padding: '0 16px', boxSizing: 'border-box', cursor: 'pointer',
            border: '1.5px solid rgba(103,80,164,0.35)', transition: 'all 0.2s',
            '&:hover': { background: 'rgba(103,80,164,0.06)', borderColor: '#6750A4' },
            whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          <DownloadIcon sx={{ fontSize: 16 }} />
          Download PDF
        </Box>
      </Box>

      {/* Content */}
      {activeTab === 'mcq'        && <MCQResults          questions={mcqQuestions} />}
      {activeTab === 'subjective' && <SubjectiveResults   questions={subjectiveQuestions} />}
      {activeTab === 'coding'     && <ProgrammingResults  questions={codingQuestions} />}
    </Box>
  );
};

export default CandidateTestResults;